import React, { useState } from "react";
import { motion } from "framer-motion";
import { Leaf, MapPin, Mail, Instagram, Send, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { IMAGES, FOOTER_DATA } from "../constants/kalorenceData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "", product_interest: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setError("Please share your name, email and a little message.");
      return;
    }
    setStatus("sending");
    try {
      await axios.post(`${API}/inquiries`, form);
      setStatus("done");
      setForm({ name: "", email: "", message: "", product_interest: "" });
    } catch (err) {
      console.error(err);
      setStatus("error");
      setError("Something went wrong sending your note. Please try again.");
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#F7F5F0] min-h-screen pt-20">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-5rem)]">
        {/* Left — evocative image + details */}
        <div className="relative overflow-hidden min-h-[420px]">
          <img
            src={IMAGES.img_30}
            alt="Kalorence dupatta on bamboo ladder"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2B3A2F]/95 via-[#2B3A2F]/55 to-[#2B3A2F]/30" />
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative h-full flex flex-col justify-end p-8 sm:p-14 text-[#F7F5F0]"
          >
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#E2DACF] mb-3 flex items-center gap-2">
              <Leaf className="w-4 h-4 text-[#C47D65]" /> Say Hello
            </p>
            <h1 className="font-cormorant text-5xl sm:text-6xl font-light leading-tight mb-8">
              Let's talk botanicals
            </h1>
            <ul className="space-y-4 text-sm font-light">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#C47D65] shrink-0 mt-0.5" />
                {FOOTER_DATA.address}
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#C47D65] shrink-0" />
                <a href={`mailto:${FOOTER_DATA.email}`} className="hover:text-[#C47D65] transition-colors">
                  {FOOTER_DATA.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Instagram className="w-5 h-5 text-[#C47D65] shrink-0" />
                <a
                  href={FOOTER_DATA.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#C47D65] transition-colors"
                >
                  @kalorence
                </a>
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Right — form */}
        <div className="flex items-center justify-center p-8 sm:p-14">
          <div className="w-full max-w-md">
            {status === "done" ? (
              <motion.div
                data-testid="contact-success-message"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center space-y-5 py-10"
              >
                <CheckCircle2 className="w-16 h-16 text-[#C47D65] mx-auto" />
                <h2 className="font-cormorant text-3xl font-light">Your note is on its way</h2>
                <p className="text-sm text-[#5C6B5F] font-light leading-relaxed">
                  Thank you for reaching out. We read every message by hand from our Jayanagar
                  studio and will write back soon.
                </p>
                <button
                  data-testid="contact-send-another"
                  onClick={() => setStatus("idle")}
                  className="text-xs tracking-[0.2em] uppercase font-semibold border-b border-[#2B3A2F] pb-1 hover:text-[#C47D65] hover:border-[#C47D65] transition-colors"
                >
                  Send another note
                </button>
              </motion.div>
            ) : (
              <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#C47D65]">
                    Inquiry
                  </p>
                  <h2 className="font-cormorant text-3xl font-light mt-2">
                    Send us a message
                  </h2>
                </div>

                {error && (
                  <div
                    data-testid="contact-error-message"
                    className="p-3 bg-red-50 border border-red-200 text-red-600 text-xs"
                  >
                    {error}
                  </div>
                )}

                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-widest text-[#5C6B5F] font-semibold">
                    Your Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    data-testid="contact-name-input"
                    value={form.name}
                    onChange={handleChange}
                    className="w-full bg-transparent border-b border-[#2B3A2F]/30 py-2.5 text-sm focus:border-[#2B3A2F] outline-none transition-colors"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-widest text-[#5C6B5F] font-semibold">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    data-testid="contact-email-input"
                    value={form.email}
                    onChange={handleChange}
                    className="w-full bg-transparent border-b border-[#2B3A2F]/30 py-2.5 text-sm focus:border-[#2B3A2F] outline-none transition-colors"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-widest text-[#5C6B5F] font-semibold">
                    Interested In (optional)
                  </label>
                  <input
                    type="text"
                    name="product_interest"
                    data-testid="contact-interest-input"
                    value={form.product_interest}
                    onChange={handleChange}
                    placeholder="Dupattas, totes, a custom piece..."
                    className="w-full bg-transparent border-b border-[#2B3A2F]/30 py-2.5 text-sm focus:border-[#2B3A2F] outline-none transition-colors placeholder:text-[#5C6B5F]/40"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-widest text-[#5C6B5F] font-semibold">
                    Your Message
                  </label>
                  <textarea
                    name="message"
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={handleChange}
                    rows="4"
                    className="w-full bg-transparent border-b border-[#2B3A2F]/30 py-2.5 text-sm focus:border-[#2B3A2F] outline-none transition-colors resize-none"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={status === "sending"}
                  className="w-full bg-[#2B3A2F] text-[#F7F5F0] py-4 hover:bg-[#1E2922] transition-all text-xs tracking-[0.2em] uppercase font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {status === "sending" ? "Sending..." : (
                    <>
                      Send Message <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
